import { Component, EventEmitter, OnInit, Output, QueryList, ViewChildren, ViewContainerRef, OnDestroy, Input, OnChanges, SimpleChanges, Injector, ViewChild, AfterViewInit } from '@angular/core';
import { MatRow, MatTableDataSource } from '@angular/material/table';
import { Observable, Subscription } from 'rxjs';
import { ScfV2Service } from '../scf-v2.service';
import { SCFMastersUpdate } from '../store/scf-master/scf-masters.action';
import { FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { utils } from 'src/app/modules/libs/utils';
import { SCFMaster } from '../scf';
import { BaseComponent } from 'src/app/common/base/base.component';
import { MatSort } from '@angular/material/sort';

@Component({
  selector: 'scf-v2-side',
  templateUrl: './scf-v2-side.component.html',
  styleUrls: ['./scf-v2-side.component.scss']
})
export class ScfV2SideComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  @Input() filter?: string | null;
  @Output() rowSelected = new EventEmitter<any>();

  @ViewChildren('matrow', { read: ViewContainerRef }) rows?: QueryList<ViewContainerRef>;
  @ViewChildren('matrow') datarows?: QueryList<MatRow>;

  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = [
    'serialNo',
    'locationNames',
    'statusName',
  ];

  dataSource = new MatTableDataSource<SCFMaster>();

  scfMasters: SCFMaster[] = [];
  scfMasters$!: Observable<SCFMaster[]>;
  scfMastersSubs: Subscription = new Subscription;
  scfMaster?: SCFMaster | null;

  serialNo!: string;
  selectedRowIndex?= -1;
  selectedIndex = -1;
  showClosed = false;

  allowNew = false;
  disabled = false;
  maxHeight!: string;

  constructor(
    private service: ScfV2Service,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngAfterViewInit(): void {
    this.setSort<SCFMaster>(this.dataSource, this.sort);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      this.applyFilter();
    }
  }

  /**
   * Rebuilds the SCF table after requester data becomes available so requester-name filters work on initial load.
   */
  override usersLoaded(): void {
    // Users and SCFs load independently, so refresh the predicate when users arrive after the table.
    if (this.scfMasters?.length && this.dataSource) {
      this.createDataSource();
    }
  }

  override ngOnDestroy(): void {
    this.scfMastersSubs?.unsubscribe();
  }

  ngOnInit(): void {
    this.loadSCFs();
  }

  dataSourceCreated?: boolean;

  loadSCFs() {
    this.scfMasters$ = this.store.select(state => state.SCFMasters.data);
    this.scfMastersSubs = this.scfMasters$.subscribe(async data => {
      if (!data?.length) return;
      this.scfMasters = data.filter(x => x.serialNo !== '');

      this.createDataSource();

      const openDocument = await this.checkOpenDocument<SCFMaster>(FormType.SCF, data);
      if (openDocument) {
        this.showClosed = openDocument.closed ?? this.showClosed;
        if (openDocument.document.id != this.scfMaster?.id)
          this.getValues(openDocument.document);
      }
    });
  }

  /**
   * Rebuilds the side table while preserving the active search term after the SCF store refreshes.
   */
  createDataSource() {
    const currentFilter = this.filter?.trim().toLowerCase() ?? '';
    const filteredData = this.scfMasters?.filter(x =>
      ((x.status !== FormStatusEnum.Canceled && x.status !== FormStatusEnum.Closed) && !this.showClosed) || this.showClosed
    ).sort((a, b) => utils.sort(a.serialNo, b.serialNo, false));
    // Use amendment statuses from the list payload so canceled history does not display the amendment icon.
    filteredData?.forEach(scf => {
      const amendmentStatuses = scf.scfAmendmentStatuses ?? scf.scfAmendments?.map(amendment => amendment.status);
      scf.scfAmendmentsCount = amendmentStatuses
        ? amendmentStatuses.filter(status => status > 0).length
        : scf.scfAmendmentsCount ?? 0;
    });

    this.dataSource = new MatTableDataSource(filteredData);
    // Reapply the predicate and filter because selecting a form replaces the table data source.
    this.createDataSourceSort();
    this.dataSource.filter = currentFilter;
  }

  createDataSourceSort() {
    this.dataSource.filterPredicate = (data: SCFMaster, filter: string) => {
      const term = filter.trim().toLowerCase();

      // Lookup requester name
      const requester = this.users.find(u => u.id === data.requesterId);
      const requesterName = requester?.name?.toLowerCase() ?? '';

      // Combine searchable fields
      const combined = [
        data.serialNo,
        data.formStatus?.name,
        requesterName,
        data.description,
      ].join(' ').toLowerCase();

      // Handle negative filter with "-" symbol
      if (term.startsWith('-')) {
        const negativeTerm = term.slice(1); // remove the "-"
        return !combined.includes(negativeTerm);
      }

      return combined.includes(term);
    };

    this.setSort<SCFMaster>(this.dataSource, this.sort);
  }

  applyFilter() {
    this.createDataSource();
    this.dataSource.filter = this.filter?.trim().toLowerCase() ?? '';
  }

  rowUnselected() {
    this.scfMaster = null;
    this.selectedRowIndex = -1;
    this.rowSelected.emit(null);
  }

  async rowClicked(row: SCFMaster) {
    if (await this.canDeactivate()) {
      if (this.scfMaster?.id == row.id) {
        this.rowUnselected();
      }
      else {
        this.getValues(row);
      }
    }
  }

  async getValues(row: SCFMaster) {
    this.selectedIndex = this.dataSource.data.findIndex(x => x.id === row.id);
    if (row?.id) {
      this.setOpenDocument(FormType.SCF, row.id);
      this.scfMaster = await this.getSCFMasterByID(row.id);
      if (this.scfMaster && this.scfMaster?.id) {
        this.store.dispatch(new SCFMastersUpdate(this.scfMaster?.id, this.scfMaster));
        this.selectedRowIndex = this.scfMaster?.id;
        this.rowSelected.emit(this.scfMaster);
        this.showElement(this.selectedIndex, 7);
      }
    }
  }

  getSCFMasterByID(id: number): Promise<SCFMaster> {
    return new Promise((resolve, reject) => {
      this.service?.getById(id).toPromise().then(scfMaster => {
        if (scfMaster) {
          scfMaster.retrievedBy = this.getCurrentUser()?.id;
          resolve(scfMaster);
        }
      });
    });
  }

  showDocument(e: SCFMaster) {
    this.getValues(e);
  }

  // #region TableScroll
  showElement(index: number, height: number) {
    const indexstr = index.toString();
    const row = this.rows?.find(
      (r) => r.element.nativeElement.getAttribute('position') === indexstr
    );
    if (row) {
      const rect = row.element.nativeElement.getBoundingClientRect();
      if (rect.y <= 0 || rect.y + rect.height > height) {
        row.element.nativeElement.scrollIntoView(false, {
          behavior: 'instant',
        });
      }
      return;
    }
    console.log('not found');
  }

  getPosition(row: any): number {
    const i = this.dataSource.data.findIndex((r) => {
      return r.id === row.id;
    });
    return i;
  }

  formatLocationNames(locationNames: string) {
    if (locationNames) {
      return utils.replaceAll(locationNames, ',', ', ');
    }
    return '';
  }


}
